import { FC, useCallback, useEffect, useState } from "react";
import ".././Jobs.scss";
import { useAppDispatch, useAppSelector } from "../../../../store";
import CandidateJobHeader from "../CandidateJobHeader";
import CandidateVacancyCard from "../CandidateVacancyCard";
import {
  DEFAULT_ITEMS_PER_PAGE,
  DEFAULT_SEARCH_INPUT_DEBOUNCE_DELAY,
  DEFAULT_SEARCH_INPUT_LENGTH,
  ITEMS_PER_PAGE_OPTIONS,
} from "../../../../constants/app";

import PaginationControls from "../../../../utils/PaginationControls";
import { fetchHistoryListings } from "../../../../services/candidateJobsService";
import ActivationAlert from "../Elements/ActivationAlert";
import SortControls from "../Elements/SortControls";
import SearchAndFilter from "./SearchAndFilter";
import useDebouncedValue from "../../../../hooks/useDebouncedValue";
import { usePrevious } from "../../../../hooks/usePrevious";
import { getAllPositions } from "../../../../store/vacancy/thunks";
import { getCandidateCentres } from "../../../../store/candidate/thunks";
import ExpiringNotifications from "../Elements/ExpiringNotification";

/**
 * The `HistoryListing` component is responsible for displaying a paginated and sortable
 * list of job history listings for a candidate. It includes features such as sorting,
 * pagination, and dynamic updates based on user interactions.
 *
 * @component
 *
 * @description
 * This component fetches and displays the candidate's job history listings, allowing
 * users to sort the listings by specific fields, change the number of items displayed
 * per page, and navigate through pages. It uses Redux for state management and React
 * hooks for handling side effects and state updates.
 *
 * @dependencies
 * - `useAppDispatch`: A custom hook to dispatch Redux actions.
 * - `useAppSelector`: A custom hook to access Redux state.
 * - `fetchHistoryListings`: A function to fetch job history listings from the API.
 * - `ActivationAlert`: A component to display activation alerts.
 * - `CandidateJobHeader`: A component to display job-related statistics.
 * - `SortControls`: A component to handle sorting options.
 * - `CandidateVacancyCard`: A component to display individual job vacancies.
 * - `PaginationControls`: A component to handle pagination.
 *
 * @state
 * - `itemPerPageList` (`number`): The number of items to display per page.
 * - `currentPage` (`number`): The current page number for pagination.
 * - `totalPages` (`number`): The total number of pages available.
 * - `paginationLinks` (`any[]`): The pagination links for navigating pages.
 * - `sortBy` (`string`): The field by which the items are sorted.
 * - `sortOrder` (`string`): The order in which the items are sorted (ascending or descending).
 *
 * @effects
 * - Fetches job history listings when the component mounts or when pagination, sorting,
 *   or items per page change.
 * - Updates pagination and sorting state based on user interactions.
 *
 * @methods
 * - `getCandidateJobList`: Fetches the candidate job history listings based on the current
 *   state of pagination and sorting.
 * - `handlePageChange`: Updates the current page based on the selected pagination link.
 * - `handlePageSizeChange`: Updates the number of items per page and resets to the first page.
 * - `handleTimeFrameSort`: Toggles the sorting order and updates the sorting field.
 *
 * @returns {JSX.Element} The rendered `HistoryListing` component.
 */
const HistoryListing: FC = () => {
  const dispatch = useAppDispatch();

  const verificationStatus = useAppSelector(
    (state) => state.onboarding.personaldetails?.verification_status
  );
  const documentsExpired = useAppSelector(
    (state) => state.onboarding.userpersonaldetails?.is_documents_expired
  );
  const candidateJobsList = useAppSelector(
    (state) => state.candidateJobs.candidateHistoryJobsList
  );
  const { positions } = useAppSelector((state) => state.vacancy);

  const [itemPerPageList, setItemPerPageList] = useState<number>(
    DEFAULT_ITEMS_PER_PAGE
  );
  const candidateJobsListData = candidateJobsList?.history_jobs;
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [paginationLinks, setPaginationLinks] = useState<any[]>([]);
  const [sortBy, setSortBy] = useState<string>("created_at");
  const [sortOrder, setSortOrder] = useState<string>("desc");
  // Search and debouncing states
  const [keyword, setKeyword] = useState("");
  const [selectedCentreIds, setSelectedCentreIds] = useState<number[]>([]);
  const [selectedPositionIds, setSelectedPositionIds] = useState<number[]>([]);
  const [searchInput, setSearchInput] = useState<string>("");

  const debouncedSearch = useDebouncedValue(
    searchInput,
    DEFAULT_SEARCH_INPUT_DEBOUNCE_DELAY
  );

  const currentParams = {
    keyword,
    selectedCentreIds,
    selectedPositionIds,
    currentPage,
    itemPerPageList,
    sortBy,
    sortOrder,
  };
  const previousParams = usePrevious(currentParams);
  /**
   * Fetches the candidate job history listings based on the current page,
   * items per page, sorting order, and sorting field. This function is
   * memoized using `useCallback` to prevent unnecessary re-renders.
   *
   * @async
   * @function
   * @returns {Promise<void>} A promise that resolves when the job history
   * listings are successfully fetched and dispatched.
   *
   * @dependencies
   * - `dispatch`: The dispatch function to trigger the fetch action.
   * - `currentPage`: The current page number for pagination.
   * - `itemPerPageList`: The number of items to display per page.
   * - `sortOrder`: The order in which the items should be sorted (e.g., ascending or descending).
   * - `sortBy`: The field by which the items should be sorted.
   */
  const getCandidateJobList = useCallback(async () => {
    if (positions.length <= 0) {
      await fetchPositions();
      await fetchCentres();
    }
    const prev = previousParams;
    const areSame =
      prev &&
      prev.keyword === currentParams.keyword &&
      prev.currentPage === currentParams.currentPage &&
      prev.itemPerPageList === currentParams.itemPerPageList &&
      prev.sortBy === currentParams.sortBy &&
      prev.sortOrder === currentParams.sortOrder &&
      prev.selectedCentreIds === currentParams.selectedCentreIds &&
      prev.selectedPositionIds === currentParams.selectedPositionIds;

    if (areSame) {
      return; // Skip if no actual change
    }

    await fetchHistoryListings(dispatch, {
      page: currentPage,
      per_page: itemPerPageList,
      sort_order: sortOrder,
      sort_by: sortBy,
      positions: selectedPositionIds,
      keyword: keyword,
      centre_ids: selectedCentreIds,
    });
  }, [
    dispatch,
    currentPage,
    itemPerPageList,
    keyword,
    sortBy,
    sortOrder,
    positions,
    selectedCentreIds,
    selectedPositionIds,
  ]);

  /**
   * Fetches the candidate job history listings when the component mounts or when the
   * `currentPage` or `itemPerPageList` state changes.
   *
   * @effect
   * - Calls the `getCandidateJobList` function to fetch the job history listings.
   */
  useEffect(() => {
    getCandidateJobList();
  }, [currentPage, itemPerPageList, getCandidateJobList]);

  /**
   * Handles the change in the number of items per page by updating the `itemPerPageList` state.
   *
   * @param value - The numeric value representing the number of items per page.
   */
  const handlePageChange = (url: string | null) => {
    if (url) {
      const page = new URL(url).searchParams.get("page");
      setCurrentPage(Number(page));
    }
  };

  /**
   * Updates the current page and items per page when the component mounts or when the `candidatesList` changes.
   */
  useEffect(() => {
    if (candidateJobsListData) {
      setTotalPages(candidateJobsListData?.last_page || 1);
      setPaginationLinks(candidateJobsListData?.links);
    }
  }, [candidateJobsListData]);

  /*
   * Handles the change in the number of items per page by updating the `itemPerPageList` state.
   *
   * @param value - The numeric value representing the number of items per page.
   */
  const handlePageSizeChange = (value: string | number) => {
    setItemPerPageList(Number(value));
    setCurrentPage(1); // Reset to the first page when changing items per page
  };

  /**
   * Handles the sorting of the job listings based on the selected field and order.
   * @param type - The type of sorting to be applied (e.g., "created_at").
   */
  const handleTimeFrameSort = (type: string) => {
    setSortBy(type);
    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
  };

  const fetchPositions = async () => {
    await dispatch(getAllPositions());
  };

  const fetchCentres = async () => {
    await dispatch(getCandidateCentres());
  };

  // Update debounced keyword and reset page when typing stops
  useEffect(() => {
    if (
      debouncedSearch.length === 0 ||
      debouncedSearch.length > DEFAULT_SEARCH_INPUT_LENGTH
    ) {
      setKeyword(debouncedSearch);
      setCurrentPage(1); // reset to first page on new search
    }
  }, [debouncedSearch]);

  return (
    <>
      <div className="d-flex flex-column gap-20">
        <ActivationAlert />
        { verificationStatus && verificationStatus === "verified" ? <ExpiringNotifications /> : "" }
        <CandidateJobHeader
          jobListingCount={candidateJobsList?.total_job_listing}
          pendingJobCount={candidateJobsList?.total_pending_jobs}
          acceptedJobCount={candidateJobsList?.total_accepted_jobs}
          isCandidateFilledAllForms={candidateJobsList?.is_user_step_exists}
          documentsExpired={documentsExpired}
        />
        <SearchAndFilter
          keyword={searchInput}
          selectedCentreIds={selectedCentreIds}
          selectedPositionIds={selectedPositionIds}
          onKeywordChange={setSearchInput}
          onCentreIdsChange={setSelectedCentreIds}
          onPositiondsChange={setSelectedPositionIds}
        />
        <div className="d-flex flex-column">
          {/** Lissting header */}
          <SortControls
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortChange={handleTimeFrameSort}
            page_type="history"
          />
          {/** Listing data */}
          {candidateJobsListData && candidateJobsListData?.data?.length > 0 ? (
            candidateJobsListData?.data?.map((vacancy, index) => (
              <CandidateVacancyCard
                key={index}
                vacancy={vacancy}
                type="history"
                isCandidateFilledAllForms={
                  candidateJobsList?.is_user_step_exists
                }
                documentsExpired={documentsExpired}
              />
            ))
          ) : (
            <div className="d-flex gap-20 align-items-center justify-content-center text-center p-15px">
              <div className="fs-16 fw-400 text-grey-medium">No Jobs Found</div>
            </div>
          )}
        </div>
        {/* Pagination Component */}
        {candidateJobsList &&
          candidateJobsList?.history_jobs?.data?.length >
            DEFAULT_ITEMS_PER_PAGE && (
            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              paginationLinks={paginationLinks}
              itemsPerPage={itemPerPageList}
              itemsPerPageOptions={ITEMS_PER_PAGE_OPTIONS}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handlePageSizeChange}
            />
          )}
        {/* Pagination Component */}
      </div>
    </>
  );
};

export default HistoryListing;
